import { useEffect, useRef, useState } from "react";
import { socialPlatforms } from "../../../mocks/gameData";

export default function SocialMedia() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="socials" ref={ref} className="relative py-28 bg-[#0a0814] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent opacity-30" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(168,85,247,0.05)_0%,_transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className={`flex flex-col items-center text-center mb-16 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="font-mono-tech text-xs text-[#22d3ee] uppercase tracking-widest mb-3">// connect with me</span>
          <h2 className="font-orbitron text-3xl md:text-5xl font-black uppercase text-white mb-2">
            Social <span className="neon-text-cyan">Media</span>
          </h2>
          <p className="font-rajdhani text-base text-slate-400 max-w-xl">
            Follow across platforms for daily content, live gaming sessions, and behind the scenes.
          </p>
        </div>

        {/* Total combined followers */}
        <div
          className={`flex items-center justify-center gap-2 mb-14 transition-all duration-700 delay-100 ${
            visible ? "opacity-100 scale-100" : "opacity-0 scale-90"
          }`}
        >
          <div className="card-gaming rounded-2xl px-10 py-5 text-center border-[#e879f9]/30">
            <div className="font-orbitron text-4xl font-black neon-text-fuchsia animate-glow-pulse">450K+</div>
            <div className="font-rajdhani text-sm uppercase tracking-widest text-slate-400 mt-1">Combined Community</div>
          </div>
        </div>

        {/* Platform Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {socialPlatforms.map((platform, i) => (
            <a
              key={platform.id}
              href={platform.url}
              className={`card-gaming rounded-xl p-6 group block cursor-pointer transition-all duration-700 no-underline ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${i * 80}ms`, textDecoration: "none" }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${platform.color}15`, border: `1px solid ${platform.color}30` }}
                  >
                    <i
                      className={`${platform.icon} text-2xl`}
                      style={{ color: platform.color }}
                    />
                  </div>
                  <div>
                    <p className="font-orbitron text-xs font-bold text-white">{platform.platform}</p>
                    <p className="font-mono-tech text-xs text-slate-500">{platform.handle}</p>
                  </div>
                </div>
                <i
                  className="ri-external-link-line text-slate-600 group-hover:text-[#e879f9] transition-colors duration-300 text-sm"
                />
              </div>

              <p className="font-rajdhani text-sm text-slate-400 leading-relaxed mb-4">{platform.description}</p>

              <div className="flex items-center justify-between pt-3 border-t border-white/5">
                <div>
                  <div className="font-orbitron text-lg font-black text-white">{platform.followers}</div>
                  <div className="font-mono-tech text-xs text-slate-500 capitalize">{platform.stat}</div>
                </div>
                <div
                  className="font-mono-tech text-xs px-2 py-1 rounded"
                  style={{ color: platform.color, backgroundColor: `${platform.color}15`, border: `1px solid ${platform.color}20` }}
                >
                  {platform.extra}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
